/* eslint-disable no-unused-vars */
import React, { useState, useMemo, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import { TableContainer, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { styled } from "@mui/system";
import styles from './intr.module.css';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ExpandRight from "../../../../../assets/images/expandRight.svg";
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CustomPagination from 'Samarth Module/components/CustomPagination/CustomPagination';
import { INTR_expandCollapseColumns } from '../LossToActionStatic';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    "&.MuiTableCell-head": {
        fontFamily: "Unilever Shilling",
        backgroundColor: "#3447d4",
        color: "#fff",
        textAlign: "center",
        padding: "5px",
        fontSize: "12px",
        fontWeight: 400,
        border: "1px solid #EAECF0",
        whiteSpace: "normal",
        wordBreak: "break-word",
    },
    "&.MuiTableCell-body": {
        fontFamily: "Unilever Shilling",
        fontSize: 12,
        fontWeight: 400,
        padding: "5px",
        color: "#2B2B2B",
        border: "1px solid #EAECF0",
    },
}));

/* ✅ K / L / Cr FORMATTER */
const formatToCappedUnit = (value) => {
    if (value === null || value === undefined) return "-";

    const numValue = Number(value);

    if (Number.isNaN(numValue)) return "-";
    if (numValue === 0) return "0";

    if (numValue < 1000) {
        return numValue.toLocaleString("en-IN", {
            maximumFractionDigits: 2,
        });
    }

    if (numValue < 100000) {
        return `${(numValue / 1000).toFixed(1)} K`;
    }

    if (numValue < 10000000) {
        return `${(numValue / 100000).toFixed(1)} L`;
    }

    return `${(numValue / 10000000).toFixed(1)} Cr`;
};

/* ✅ SMART FORMATTER */
const formatValue = (value) => {
    if (value === null || value === undefined) return "-";

    if (typeof value === "number") {
        if (value >= 10000) {
            return formatToCappedUnit(value);
        }

        return value.toLocaleString("en-IN", {
            maximumFractionDigits: 2,
        });
    }

    return value;
};

const isPercentageColumn = (label = "") =>
    String(label).trim().startsWith("%");

const formatPercentageValue = (value) => {
    if (value === null || value === undefined) return "-";

    const numValue = Number(value);

    if (Number.isNaN(numValue)) return "-";

    return `${numValue.toLocaleString("en-IN", {
        maximumFractionDigits: 2,
    })}%`;
};

const normalizeLabel = (label = "") => String(label).trim().toLowerCase();

const labelsEqual = (a = "", b = "") => normalizeLabel(a) === normalizeLabel(b);

const isObjectLike = (value) =>
    value !== null && typeof value === "object" && !Array.isArray(value);

const EXPAND_COLUMN_WIDTH = 64;

const getValueByLabel = (obj, label) => {
    if (!isObjectLike(obj)) return undefined;

    if (obj[label] !== undefined) return obj[label];

    const matchedKey = Object.keys(obj).find((key) => labelsEqual(key, label));
    return matchedKey ? obj[matchedKey] : undefined;
};

const StyledTableRow = styled(TableRow)({});

const getNextSortDirection = (isCurrentColumn, currentDirection, defaultDirection = 'DESC') => {
    if (!isCurrentColumn) return defaultDirection;
    return currentDirection === 'ASC' ? 'DESC' : 'ASC';
};

const renderSortIndicator = (isActive, direction) => {
    if (!isActive) {
        return (
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    opacity: 0.5,
                }}
            >
                <ArrowDropUpIcon
                    sx={{
                        fontSize: 18,
                        color: '#fff',
                        marginBottom: '-8px',
                    }}
                />
                <ArrowDropDownIcon sx={{ fontSize: 18, color: '#fff' }} />
            </div>
        );
    }

    if (direction === 'ASC') {
        return <ArrowDropUpIcon sx={{ fontSize: 18, color: '#fff' }} />;
    }

    return <ArrowDropDownIcon sx={{ fontSize: 18, color: '#fff' }} />;
};

const renderColoredSortIndicator = (isActive, direction, color) => {
    if (!isActive) {
        return (
            <span
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    opacity: 0.5,
                }}
            >
                <ArrowDropUpIcon
                    sx={{
                        fontSize: 18,
                        color: `${color} !important`,
                        marginBottom: '-8px',
                    }}
                />
                <ArrowDropDownIcon
                    sx={{
                        fontSize: 18,
                        color: `${color} !important`,
                    }}
                />
            </span>
        );
    }

    if (direction === 'ASC') {
        return <ArrowDropUpIcon sx={{ fontSize: 18, color: `${color} !important` }} />;
    }

    return <ArrowDropDownIcon sx={{ fontSize: 18, color: `${color} !important` }} />;
};

const getExpandableColumnWidth = (isExpanded, columnLabel) => {
    if (isExpanded) return "250px";
    return labelsEqual(columnLabel, "Basepack desc") ? "13rem" : "120px";
};

const getRowFontWeight = (isFirstRow) =>
    isFirstRow ? "700 !important" : "400 !important";

/* ✅ SUB-COLUMN HELPERS */
const SUB_COLUMN_PREFIX = "Sub_";

const isSubColumn = (label) =>
    typeof label === "string" && label.startsWith(SUB_COLUMN_PREFIX);

const getSubColumnMeta = (label) => {
    if (!isSubColumn(label)) {
        return null;
    }

    const rawLabel = label.slice(SUB_COLUMN_PREFIX.length);
    const separatorIndex = rawLabel.lastIndexOf("_");

    if (separatorIndex < 0) {
        return null;
    }

    return {
        parentLabel: rawLabel.slice(0, separatorIndex).trim(),
        childLabel: rawLabel.slice(separatorIndex + 1).trim(),
    };
};

const isSubColumnOfParent = (column, parentLabel) => {
    const meta = getSubColumnMeta(column?.label);

    if (!meta) {
        return false;
    }

    return labelsEqual(meta.parentLabel, parentLabel);
};

const getGroupToggleMeta = (isParentRow, hasChildren, isExpanded) => {
    const isExpandable = isParentRow && hasChildren;

    if (!isExpandable) {
        return {
            isExpandable,
            title: "",
            ariaLabel: "",
            Icon: null,
        };
    }

    return {
        isExpandable,
        title: isExpanded ? "Collapse" : "Expand",
        ariaLabel: isExpanded ? "Collapse group" : "Expand group",
        Icon: isExpanded ? KeyboardArrowDownIcon : KeyboardArrowRightIcon,
    };
};

const getNestedColumnDisplay = (columnLabel, nestedValue) => {
    const isPercent = isPercentageColumn(columnLabel);

    if (isPercent) {
        const formattedPercentage = formatPercentageValue(nestedValue);
        return {
            title: formattedPercentage,
            value: formattedPercentage,
        };
    }

    return {
        title:
            typeof nestedValue === "number"
                ? nestedValue.toLocaleString("en-IN")
                : String(nestedValue ?? ""),
        value: formatValue(nestedValue),
    };
};

const TableShimmerBlock = ({ colSpan }) => (
    <TableRow>
        <StyledTableCell colSpan={colSpan} sx={{ padding: 0 }}>
            <div className={styles.tableShimmerBlock} />
        </StyledTableCell>
    </TableRow>
);

const INTROverviewTable = ({
    data,
    columns,
    paginationData,
    sortingData,
    handlePageChange,
    isLoading = false,
}) => {
    const [tableData, setTableData] = useState(data || []);
    const [tableColumns, setTableColumns] = useState(columns || []);
    const [sortColumn, setSortColumn] = useState(sortingData?.sortBy || null);
    const [sortDirection, setSortDirection] = useState(
        sortingData?.sortOrder || "ASC"
    );
    const [fieldSection, setFieldSection] = useState(
        sortingData?.fieldsection || ""
    );
    const [page, setPage] = useState(paginationData?.page || 1);
    const [rowsPerPage, setRowsPerPage] = useState(
        paginationData?.rowsPerPage || 15
    );
    const [expandedColumns, setExpandedColumns] = useState({});
    const [expandedGrGroups, setExpandedGrGroups] = useState({});
    const [isSortingLoading, setIsSortingLoading] = useState(false);

    const fixedHeadRef = useRef(null);
    const scrollHeadRef = useRef(null);
    const fixedRowRefs = useRef([]);
    const scrollRowRefs = useRef([]);

    useEffect(() => {
        setTableData(data || []);
        setIsSortingLoading(false);
        setExpandedGrGroups({});
        fixedRowRefs.current = [];
        scrollRowRefs.current = [];
    }, [data]);

    useEffect(() => {
        setTableColumns(columns || []);
    }, [columns]);

    useEffect(() => {
        if (sortingData) {
            setSortColumn(sortingData.sortBy || null);
            setSortDirection(sortingData.sortOrder || "ASC");
            setFieldSection(sortingData.fieldsection || "");
        }
    }, [sortingData]);

    useEffect(() => {
        if (paginationData?.page) {
            setPage(paginationData.page);
        }

        if (paginationData?.rowsPerPage) {
            setRowsPerPage(paginationData.rowsPerPage);
        }
    }, [paginationData]);

    const getScrollableChildColumns = useCallback(
        (parentLabel) => {
            return (tableColumns || [])
                .filter(
                    (column) =>
                        isSubColumnOfParent(column, parentLabel) &&
                        (column.visibility ?? true)
                )
                .sort(
                    (firstColumn, secondColumn) =>
                        (firstColumn.DisplayOrder ?? 0) -
                        (secondColumn.DisplayOrder ?? 0)
                )
                .map((column) => getSubColumnMeta(column.label)?.childLabel)
                .filter(Boolean);
        },
        [tableColumns]
    );

    const hasConfiguredSubColumns = useCallback(
        (parentLabel) =>
            (tableColumns || []).some((column) =>
                isSubColumnOfParent(column, parentLabel)
            ),
        [tableColumns]
    );

    const paginatedData = useMemo(() => {
        if (paginationData?.totalRecords) {
            return tableData;
        }

        const startIndex = (page - 1) * rowsPerPage;
        const endIndex = startIndex + rowsPerPage;

        return tableData.slice(startIndex, endIndex);
    }, [tableData, page, rowsPerPage, paginationData]);

    const handleChangePage = (
        newPage,
        sortCol,
        sortDir,
        newRowsPerPage,
        fieldsection
    ) => {
        setPage(newPage);

        if (handlePageChange) {
            handlePageChange(
                newPage,
                sortCol || sortColumn,
                sortDir || sortDirection,
                newRowsPerPage || rowsPerPage,
                fieldsection || ""
            );
        }
    };

    // ✅ For nested scrollable columns:
    // sorting payload becomes:
    // sortBy: child column, fieldsection: parent column
    const handleSort = (col, direction, fieldsection = "", childCol = "") => {
        setIsSortingLoading(true);

        const actualSortColumn = fieldsection ? childCol : col.label;

        setSortColumn(actualSortColumn);
        setSortDirection(direction);
        setFieldSection(fieldsection);

        handleChangePage(
            1,
            actualSortColumn,
            direction,
            rowsPerPage,
            fieldsection
        );
    };

    // Map data keys to column labels
    const getRawDataValue = (item, columnLabel) => {
        const keyMap = {
            "GR Number": "GR Number",
            Format: "Format",
            Basepack: "Basepack",
            "Basepack desc": "Basepack desc",
            DC: "DC",
            CBU: "CBU",
            "Priority ranking": "Priority Ranking",
            "INTR Quantity": "INTR Quantity",
            "INTR Aging (hrs)": "INTR Aging (hrs)",
            "Weighted score": "Weighted Score",
            
        };

        const key = keyMap[columnLabel] || columnLabel;
        return getValueByLabel(item, key);
    };

    const getDataValue = (item, columnLabel) => {
        return formatValue(getRawDataValue(item, columnLabel));
    };

    // ✅ Get value from nested object columns
    const getNestedColumnValue = (item, columnLabel, childKey) => {
        const value = getValueByLabel(item, columnLabel);

        if (value && typeof value === "object") {
            return getValueByLabel(value, childKey) ?? 0;
        }

        return 0;
    };

    // Check if column can be expanded/collapsed for width
    const isTruncatableColumn = (label) => {
        return (
            INTR_expandCollapseColumns.includes(label) ||
            labelsEqual(label, "GR Number") ||
            labelsEqual(label, "Basepack") ||
            labelsEqual(label, "Basepack desc")
        );
    };

    // Toggle column expansion
    const toggleColumnExpansion = (columnLabel, e) => {
        e.stopPropagation();

        setExpandedColumns((prev) => ({
            ...prev,
            [columnLabel]: !prev[columnLabel],
        }));
    };

    const isHiddenColumn = (label) => labelsEqual(label, "CBU");

    const effectiveTableColumns = useMemo(() => {
        const hasGrNumberColumn = (tableColumns || []).some((col) =>
            labelsEqual(col?.label, "GR Number")
        );

        if (hasGrNumberColumn) {
            return tableColumns;
        }

        return [
            {
                label: "GR Number",
                visibility: true,
                sortable: false,
            },
            ...(tableColumns || []),
        ];
    }, [tableColumns]);

    // Split columns into fixed and scrollable
    const fixedColumns = useMemo(
        () =>
            effectiveTableColumns.filter(
                (column) =>
                    !isSubColumn(column.label) &&
                    (column.visibility ?? true) &&
                    !isHiddenColumn(column.label) &&
                    !hasConfiguredSubColumns(column.label)
            ),
        [effectiveTableColumns, hasConfiguredSubColumns]
    );

    const scrollableColumns = useMemo(
        () =>
            effectiveTableColumns.filter(
                (column) =>
                    !isSubColumn(column.label) &&
                    (column.visibility ?? true) &&
                    !isHiddenColumn(column.label) &&
                    getScrollableChildColumns(column.label).length > 0
            ),
        [effectiveTableColumns, getScrollableChildColumns]
    );

    const groupedData = useMemo(() => {
        const groups = [];
        let currentGroup = null;

        paginatedData.forEach((row, idx) => {
            const cbuValue = getValueByLabel(row, "CBU");
            const isParentRow = labelsEqual(cbuValue, "Total");

            const grNumber =
                getValueByLabel(row, "GR Number") ||
                getValueByLabel(row, "GR number") ||
                `row-${idx}`;

            if (isParentRow) {
                currentGroup = {
                    groupKey: `${grNumber}-${idx}`,
                    parentRow: row,
                    childRows: [],
                    isParentRow: true,
                };

                groups.push(currentGroup);
                return;
            }

            if (currentGroup) {
                currentGroup.childRows.push(row);
                return;
            }

            groups.push({
                groupKey: `${grNumber}-${idx}`,
                parentRow: row,
                childRows: [],
                isParentRow: false,
            });
        });

        return groups;
    }, [paginatedData]);

    const displayRows = useMemo(() => {
        const rows = [];

        groupedData.forEach((group) => {
            rows.push({
                __row: group.parentRow,
                __rowKey: `${group.groupKey}-parent`,
                __groupKey: group.groupKey,
                __isParentRow: group.isParentRow,
                __isChildRow: false,
                __hasChildren: group.childRows.length > 0,
            });

            if (group.isParentRow && expandedGrGroups[group.groupKey]) {
                group.childRows.forEach((child, childIdx) => {
                    rows.push({
                        __row: child,
                        __rowKey: `${group.groupKey}-child-${childIdx}`,
                        __groupKey: group.groupKey,
                        __isParentRow: false,
                        __isChildRow: true,
                        __hasChildren: false,
                    });
                });
            }
        });

        return rows;
    }, [groupedData, expandedGrGroups]);

    const toggleGroupExpansion = (groupKey, e) => {
        e?.stopPropagation?.();

        if (!groupKey) return;

        setExpandedGrGroups((prev) => ({
            ...prev,
            [groupKey]: !prev[groupKey],
        }));
    };

    // Handle page change from CustomPagination
    const handleCustomPageChange = (newPage, newPageSize) => {
        setPage(newPage);

        handleChangePage(
            newPage,
            sortColumn,
            sortDirection,
            newPageSize || rowsPerPage,
            fieldSection
        );
    };

    // Handle rows per page change
    const handleCustomRowsPerPageChange = (newPageSize) => {
        setRowsPerPage(newPageSize);
        setPage(1);

        handleChangePage(1, sortColumn, sortDirection, newPageSize, fieldSection);
    };

    const totalRecords = paginationData?.totalRecords || tableData.length;
    const totalPages = Math.ceil(totalRecords / rowsPerPage);

    const syncElementPairHeight = (el1, el2) => {
        if (!el1 || !el2) return;

        el1.style.height = "";
        el2.style.height = "";

        const h1 = el1.getBoundingClientRect().height;
        const h2 = el2.getBoundingClientRect().height;
        const maxHeight = Math.max(h1, h2);

        el1.style.height = `${maxHeight}px`;
        el2.style.height = `${maxHeight}px`;
    };

    const syncTableHeights = useCallback(() => {
        syncElementPairHeight(fixedHeadRef.current, scrollHeadRef.current);

        const rowCount = Math.max(
            fixedRowRefs.current.length,
            scrollRowRefs.current.length
        );

        for (let i = 0; i < rowCount; i++) {
            syncElementPairHeight(fixedRowRefs.current[i], scrollRowRefs.current[i]);
        }
    }, []);

    useLayoutEffect(() => {
        const rafId = requestAnimationFrame(() => {
            syncTableHeights();
        });

        const resizeObserver = new ResizeObserver(() => {
            requestAnimationFrame(() => {
                syncTableHeights();
            });
        });

        const elementsToObserve = [
            fixedHeadRef.current,
            scrollHeadRef.current,
            ...fixedRowRefs.current,
            ...scrollRowRefs.current,
        ].filter(Boolean);

        elementsToObserve.forEach((el) => resizeObserver.observe(el));

        window.addEventListener("resize", syncTableHeights);

        return () => {
            cancelAnimationFrame(rafId);
            resizeObserver.disconnect();
            window.removeEventListener("resize", syncTableHeights);
        };
    }, [
        syncTableHeights,
        displayRows,
        fixedColumns,
        scrollableColumns,
        expandedColumns,
        expandedGrGroups,
        isLoading,
        isSortingLoading,
    ]);

    return (
        <>
            <div className={styles.tableWrapper}>
                {/* Fixed Columns Table */}
                <TableContainer className={styles.fixedTableContainer}>
                    <Table stickyHeader>
                        <TableHead ref={fixedHeadRef}>
                            {/* First Header Row - Main Headers */}
                            <TableRow>
                                <StyledTableCell
                                    rowSpan={2}
                                    sx={{
                                        minWidth: `${EXPAND_COLUMN_WIDTH}px`,
                                        maxWidth: `${EXPAND_COLUMN_WIDTH}px`,
                                        width: `${EXPAND_COLUMN_WIDTH}px`,
                                        padding: "8px 6px !important",
                                    }}
                                />

                                {fixedColumns.map((col) => {
                                    const isExpanded = !!expandedColumns[col.label];
                                    const columnWidth = getExpandableColumnWidth(
                                        isExpanded,
                                        col.label
                                    );

                                    return (
                                    <StyledTableCell
                                        key={col.label}
                                        rowSpan={2}
                                        sx={{
                                            textAlign: "center",
                                            cursor: col.sortable ? "pointer" : "default",
                                            verticalAlign: "middle",
                                            minWidth: columnWidth,
                                            maxWidth: columnWidth,
                                            width: columnWidth,
                                        }}
                                        onClick={() => {
                                            if (!col.sortable) return;

                                            const newDirection = getNextSortDirection(
                                                sortColumn === col.label,
                                                sortDirection
                                            );

                                            handleSort(col, newDirection, "");
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                gap: 6,
                                            }}
                                        >
                                            <span
                                                style={{
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    flex: 1,
                                                }}
                                            >
                                                {col.label}
                                            </span>

                                            <div
                                                style={{
                                                    display: "flex",
                                                    gap: 2,
                                                    flexShrink: 0,
                                                }}
                                            >
                                                {col.sortable && (
                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            flexDirection: "column",
                                                            marginLeft: "4px",
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        {renderSortIndicator(sortColumn === col.label, sortDirection)}
                                                    </div>
                                                )}

                                                {isTruncatableColumn(col.label) && (
                                                    <button
                                                        onClick={(e) =>
                                                            toggleColumnExpansion(col.label, e)
                                                        }
                                                        title="Expand/Collapse column"
                                                        style={{
                                                            display: "inline-flex",
                                                            alignItems: "center",
                                                            cursor: "pointer",
                                                            background: "none",
                                                            border: "none",
                                                            padding: 0,
                                                        }}
                                                        type="button"
                                                        aria-label="Expand or collapse column"
                                                    >
                                                        <img src={ExpandRight} alt="expandRight" />
                                                    </button>
                                                    
                                                )}
                                            </div>
                                        </div>
                                    </StyledTableCell>
                                    );
                                })}
                            </TableRow>

                            <TableRow />
                        </TableHead>

                        <TableBody>
                            {isLoading || isSortingLoading ? (
                                <TableShimmerBlock colSpan={fixedColumns.length + 1} />
                            ) : (
                                displayRows.map((rowItem, idx) => {
                                    const item = rowItem.__row;
                                    const isParentRow = !!rowItem.__isParentRow;
                                    const hasChildren = !!rowItem.__hasChildren;
                                    const isFirstRow = idx === 0;
                                    const isExpanded =
                                        !!expandedGrGroups[rowItem.__groupKey];
                                    const groupToggleMeta = getGroupToggleMeta(
                                        isParentRow,
                                        hasChildren,
                                        isExpanded
                                    );
                                    const ToggleIcon = groupToggleMeta.Icon;

                                    return (
                                        <StyledTableRow
                                            key={rowItem.__rowKey || idx}
                                            ref={(el) => {
                                                fixedRowRefs.current[idx] = el;
                                            }}
                                        >
                                            <StyledTableCell
                                                sx={{
                                                    minWidth: `${EXPAND_COLUMN_WIDTH}px`,
                                                    maxWidth: `${EXPAND_COLUMN_WIDTH}px`,
                                                    width: `${EXPAND_COLUMN_WIDTH}px`,
                                                    padding: "0 6px !important",
                                                    textAlign: "left",
                                                    backgroundColor: "transparent",
                                                    fontWeight: getRowFontWeight(isFirstRow),
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 4,
                                                    }}
                                                >
                                                    <button
                                                        style={{
                                                            display: "inline-flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            width: 18,
                                                            height: 18,
                                                            cursor: groupToggleMeta.isExpandable
                                                                ? "pointer"
                                                                : "default",
                                                            background: "none",
                                                            border: "none",
                                                            padding: 0,
                                                        }}
                                                        onClick={(e) => {
                                                            if (!groupToggleMeta.isExpandable) return;
                                                            toggleGroupExpansion(
                                                                rowItem.__groupKey,
                                                                e
                                                            );
                                                        }}
                                                         type="button"
                                                         title={groupToggleMeta.title}
                                                         aria-label={groupToggleMeta.ariaLabel}
                                                         disabled={!groupToggleMeta.isExpandable}
                                                    >
                                                        {ToggleIcon ? (
                                                            <ToggleIcon
                                                                sx={{
                                                                    fontSize: 18,
                                                                    color: "#667085",
                                                                }}
                                                            />
                                                        ) : null}
                                                    </button>

                                                    {isFirstRow && (
                                                        <span
                                                            style={{
                                                                fontSize: 12,
                                                                whiteSpace: "nowrap",
                                                                fontWeight: 700,
                                                            }}
                                                        >
                                                            TOTAL
                                                        </span>
                                                    )}
                                                </div>
                                            </StyledTableCell>

                                            {fixedColumns.map((col) => {
                                                const value = getDataValue(item, col.label);
                                                const rawValue = getRawDataValue(
                                                    item,
                                                    col.label
                                                );
                                                const isExpanded =
                                                    expandedColumns[col.label];
                                                const columnWidth = getExpandableColumnWidth(
                                                    isExpanded,
                                                    col.label
                                                );

                                                return (
                                                    <StyledTableCell
                                                        key={col.label}
                                                        sx={{
                                                            minWidth: columnWidth,
                                                            maxWidth: columnWidth,
                                                            width: columnWidth,
                                                            whiteSpace: isExpanded
                                                                ? "normal"
                                                                : "nowrap",
                                                            overflow: isExpanded
                                                                ? "visible"
                                                                : "hidden",
                                                            textOverflow: isExpanded
                                                                ? "clip"
                                                                : "ellipsis",
                                                            backgroundColor: "transparent",
                                                            fontWeight: getRowFontWeight(
                                                                isFirstRow
                                                            ),
                                                        }}
                                                    >
                                                        <span
                                                            title={
                                                                typeof rawValue === "number"
                                                                    ? rawValue.toLocaleString(
                                                                          "en-IN"
                                                                      )
                                                                    : String(rawValue ?? "")
                                                            }
                                                            style={{
                                                                fontWeight: isFirstRow ? 700 : 400,
                                                            }}
                                                        >
                                                            {value}
                                                        </span>
                                                    </StyledTableCell>
                                                );
                                            })}
                                        </StyledTableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Scrollable Columns Table */}
                <TableContainer className={styles.scrollableTableContainer}>
                    <Table stickyHeader>
                        <TableHead ref={scrollHeadRef}>
                            {/* First Header Row - Main Headers */}
                            <TableRow>
                                {scrollableColumns.map((col, colIdx) => {
                                    const childColumns = getScrollableChildColumns(
                                        col.label
                                    );

                                    return (
                                        <StyledTableCell
                                            key={col.label}
                                            colSpan={childColumns.length}
                                            sx={{
                                                textAlign: "center",
                                                fontWeight: 600,
                                                backgroundColor: `${
                                                    colIdx % 2 === 0
                                                        ? "rgb(194, 224, 255)"
                                                        : "rgb(93, 110, 215)"
                                                } !important`,
                                                color: `${
                                                    colIdx % 2 === 0
                                                        ? "rgb(11, 31, 68)"
                                                        : "rgb(255, 255, 255)"
                                                } !important`,
                                            }}
                                        >
                                            <span
                                                style={{
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    display: "block",
                                                }}
                                            >
                                                {col.label}
                                            </span>
                                        </StyledTableCell>
                                    );
                                })}
                            </TableRow>

                            {/* Second Header Row - Nested Child Columns */}
                            <TableRow>
                                {scrollableColumns.map((col, colIdx) => {
                                    const childColumns = getScrollableChildColumns(
                                        col.label
                                    );

                                    return childColumns.map((childCol) => {
                                        const isCurrentSort =
                                            sortColumn === childCol &&
                                            fieldSection === col.label;

                                        return (
                                            <StyledTableCell
                                                key={`${col.label}-${childCol}`}
                                                sx={{
                                                    textAlign: "center",
                                                    minWidth: "120px",
                                                    maxWidth: "120px",
                                                    width: "120px",
                                                    cursor: col.sortable
                                                        ? "pointer"
                                                        : "default",
                                                    backgroundColor: `${
                                                        colIdx % 2 === 0
                                                            ? "rgb(194, 224, 255)"
                                                            : "rgb(93, 110, 215)"
                                                    } !important`,
                                                    color: `${
                                                        colIdx % 2 === 0
                                                            ? "rgb(11, 31, 68)"
                                                            : "rgb(255, 255, 255)"
                                                    } !important`,
                                                }}
                                                onClick={() => {
                                                    if (!col.sortable) return;

                                                    const newDirection = getNextSortDirection(
                                                        isCurrentSort,
                                                        sortDirection
                                                    );

                                                    handleSort(
                                                        col,
                                                        newDirection,
                                                        col.label,
                                                        childCol
                                                    );
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        gap: 4,
                                                        cursor: col.sortable
                                                            ? "pointer"
                                                            : "default",
                                                    }}
                                                >
                                                    {childCol}

                                                    {col.sortable &&
                                                        renderColoredSortIndicator(
                                                            isCurrentSort,
                                                            sortDirection,
                                                            colIdx % 2 === 0
                                                                ? 'rgb(11, 31, 68)'
                                                                : 'rgb(255, 255, 255)'
                                                        )}
                                                </span>
                                            </StyledTableCell>
                                        );
                                    });
                                })}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {isLoading || isSortingLoading ? (
                                <TableShimmerBlock
                                    colSpan={Math.max(
                                        scrollableColumns.reduce(
                                            (acc, col) =>
                                                acc +
                                                getScrollableChildColumns(col.label).length,
                                            0
                                        ),
                                        1
                                    )}
                                />
                            ) : (
                                displayRows.map((rowItem, idx) => {
                                    const item = rowItem.__row;
                                    const isFirstRow = idx === 0;

                                    return (
                                        <StyledTableRow
                                            key={rowItem.__rowKey || idx}
                                            ref={(el) => {
                                                scrollRowRefs.current[idx] = el;
                                            }}
                                        >
                                            {scrollableColumns.map((col) => {
                                                const childColumns =
                                                    getScrollableChildColumns(col.label);

                                                return childColumns.map((childCol) => {
                                                    const nestedValue =
                                                        getNestedColumnValue(
                                                            item,
                                                            col.label,
                                                            childCol
                                                        );
                                                    const nestedDisplay =
                                                        getNestedColumnDisplay(
                                                            col.label,
                                                            nestedValue
                                                        );

                                                    return (
                                                        <StyledTableCell
                                                            key={`${col.label}-${childCol}`}
                                                            sx={{
                                                                textAlign: "center",
                                                                minWidth: "120px",
                                                                maxWidth: "120px",
                                                                width: "120px",
                                                                overflow: "hidden",
                                                                textOverflow: "ellipsis",
                                                                whiteSpace: "nowrap",
                                                                backgroundColor: "transparent",
                                                                fontWeight:
                                                                    getRowFontWeight(
                                                                        isFirstRow
                                                                    ),
                                                            }}
                                                        >
                                                            <span
                                                                title={nestedDisplay.title}
                                                                style={{
                                                                    fontWeight: isFirstRow
                                                                        ? 700
                                                                        : 400,
                                                                }}
                                                            >
                                                                {nestedDisplay.value}
                                                            </span>
                                                        </StyledTableCell>
                                                    );
                                                });
                                            })}
                                        </StyledTableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </div>

            <CustomPagination
                page={page}
                pageSize={rowsPerPage}
                totalPages={totalPages}
                totalRecords={totalRecords}
                onPageChange={handleCustomPageChange}
                onRowsPerPageChange={handleCustomRowsPerPageChange}
            />
        </>
    );
};

TableShimmerBlock.propTypes = {
    colSpan: PropTypes.number.isRequired,
};

INTROverviewTable.propTypes = {
    data: PropTypes.arrayOf(PropTypes.object),
    columns: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string,
            visibility: PropTypes.bool,
            sortable: PropTypes.bool,
        })
    ),
    paginationData: PropTypes.shape({
        page: PropTypes.number,
        rowsPerPage: PropTypes.number,
        totalRecords: PropTypes.number,
    }),
    sortingData: PropTypes.shape({
        sortBy: PropTypes.string,
        sortOrder: PropTypes.string,
        fieldsection: PropTypes.string,
    }),
    handlePageChange: PropTypes.func,
    isLoading: PropTypes.bool,
};

export default INTROverviewTable;